import {Context} from '@segment/analytics-next';

export enum EventType {
  /**
   * Event for setting the current user's identity information.
   * To track the specific actions that cause the user to become identified
   * (i.e. clicking on the "login" or "signup" form submit button), use the
   * `UseAction` event.
   */
  UserIdentified = 'user-identified',

  /**
   * Event for when the user signs out.
   */
  UserSignedOut = 'user-signed-out',

  /**
   * Event for when the user views a page.
   */
  PageView = 'page-view',

  /**
   * Event for when the user views a modal.
   */
  ModalView = 'modal-view',

  /**
   * Event for when the user performs some trackable actions.
   */
  UserAction = 'user-action',
}

export type GenericEvent = {
  /**
   * Common context for analytics events. This value is typically only used
   * internally by the analytics backend provider to augment its logic. This
   * value is typically not sent to the analytics backend service.
   */
  context?: string;

  /**
   * Path of the current page. This value should start with a slash.
   * For example: `/login`.
   */
  pagePath?: string;
};

export type UserIdentifiedEvent = GenericEvent & {
  evtName: EventType.UserIdentified;

  /**
   * ID of the identified user.
   */
  userId: string;

  /**
   * Additional data to be sent to analytics backend service.
   */
  data?: {[key: string]: any};
};

export type UserSignedOutEvent = GenericEvent & {
  evtName: EventType.UserSignedOut;
};

export type PageViewEvent = GenericEvent & {
  evtName: EventType.PageView;

  /**
   * Name of the page.
   */
  pageName: string;

  /**
   * Additional data to be sent to analytics backend service.
   */
  data?: {[key: string]: any};
};

export type ModalViewEvent = GenericEvent & {
  evtName: EventType.ModalView;

  /**
   * Name of the modal.
   */
  modalName: string;

  /**
   * Additional data to be sent to analytics backend service.
   */
  data?: {[key: string]: any};
};

export type UserActionEvent = GenericEvent & {
  evtName: EventType.UserAction;

  /**
   * Name of the action the user performed.
   */
  actionName: string;

  /**
   * Additional data to be sent to analytics backend service.
   */
  data?: {[key: string]: any};
};

/**
 * A collection of all acceptable analytics event types for the `track` method
 * of `AnalyticsContext`.
 *
 * Additional event types should be OR'ed to this list:
 *
 * ```
 * type AnalyticsEvent =
 *  | Event1
 *  | Event2
 *  | ...;
 * ```
 */
type AnalyticsEvent =
  | GenericEvent
  | UserIdentifiedEvent
  | UserSignedOutEvent
  | PageViewEvent
  | ModalViewEvent
  | UserActionEvent;

export interface AnalyticsContext {
  track<T extends AnalyticsEvent = AnalyticsEvent>(payload: T): Promise<Context | void>;
}

export type AnalyticsConsumerOptions = {
  context?: string;
};
